// The approved nine-direction header and three-pose mail illustrations.
// Sprite coordinates refer to the original sheets; CDN sizing preserves their ratios.
const HEAD_CENTERS = [[211,230],[627,232.5],[1043,232],[218.5,621],[627,623.5],[1036.5,622],[215,1014],[626.5,1016.5],[1039.5,1012.5]];
const HEAD_NAMES = ['upper left','up','upper right','left','neutral','right','lower left','down','lower right'];
const HEAD_SECTORS = [5,8,7,6,3,0,1,2];
const MAIL_X = [.1367053998632946,49.555707450444295,99.04306220095694];

class FdHeaderDuck extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.pointer = matchMedia('(any-hover: hover) and (any-pointer: fine)');
    this.inView = true;
    this.point = null;
    this.raf = 0;
    this.draw(4);
    if (this.dataset.followCursor === 'false') return;
    document.addEventListener('pointermove', event => {
      if (event.pointerType === 'touch' || this.motion.matches || !this.pointer.matches || !this.inView || document.hidden) return;
      this.point = {x:event.clientX,y:event.clientY};
      this.queue();
    }, {passive:true,signal});
    document.documentElement.addEventListener('pointerleave', () => this.reset(), {signal});
    window.addEventListener('blur', () => this.reset(), {signal});
    document.addEventListener('visibilitychange', () => this.reset(), {signal});
    // Horizon scrolls .page-wrapper on desktop, so listen in the capture phase.
    window.addEventListener('scroll', () => this.queue(), {passive:true,capture:true,signal});
    window.addEventListener('resize', () => this.queue(), {passive:true,signal});
    for (const query of [this.motion,this.pointer]) query.addEventListener('change', () => this.reset(), {signal});
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (!this.inView) this.reset();
      });
      this.observer.observe(this);
    }
  }
  draw(frame) {
    const [cx,cy] = HEAD_CENTERS[frame];
    this.style.backgroundPosition = `${(cx-194)/866*100}% ${(cy-194)/866*100}%`;
    this.dataset.frame = frame;
    this.dataset.direction = HEAD_NAMES[frame];
  }
  frameAt() {
    const r = this.getBoundingClientRect();
    const dx = this.point.x-r.left-r.width/2, dy = this.point.y-r.top-r.height/2;
    const previous = Number(this.dataset.frame ?? 4);
    const deadZone = Math.max(40,r.width*.58), distance = Math.hypot(dx,dy);
    if (distance < deadZone || (previous === 4 && distance < deadZone+12)) return 4;
    const angle = Math.atan2(dy,dx), sector = HEAD_SECTORS.indexOf(previous);
    if (sector >= 0) {
      const delta = Math.atan2(Math.sin(angle-sector*Math.PI/4),Math.cos(angle-sector*Math.PI/4));
      if (Math.abs(delta) < Math.PI/8+.13) return previous;
    }
    return HEAD_SECTORS[((Math.round(angle/(Math.PI/4))%8)+8)%8];
  }
  queue() {
    if (this.raf || !this.point) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      if (!this.isConnected || !this.point) return;
      this.draw(!document.hidden && this.inView && !this.motion.matches && this.pointer.matches ? this.frameAt() : 4);
    });
  }
  reset() { cancelAnimationFrame(this.raf);this.raf=0;this.point=null;this.draw(4); }
  disconnectedCallback() { this.reset();this.abort?.abort();this.observer?.disconnect(); }
}

class FdDispatch extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.sprite = this.querySelector('[data-post-duck]');
    this.form = this.querySelector('form');
    this.email = this.querySelector('input[type="email"]');
    this.trigger = this.querySelector('.fd-post-illustration');
    if (!this.sprite || !this.trigger) return;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.pointer = matchMedia('(any-hover: hover) and (any-pointer: fine)');
    this.timers = [];
    this.frame = 0;
    this.syncExpression();
    this.trigger.addEventListener('pointerenter', e => this.offer(e), {signal});
    this.trigger.addEventListener('pointerleave', () => this.lower(), {signal});
    this.trigger.addEventListener('pointercancel', () => this.reset(), {signal});
    for (const name of ['input','change']) this.email?.addEventListener(name, () => this.syncExpression(), {signal});
    this.form?.addEventListener('reset', () => {
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => this.syncExpression(),0);
    }, {signal});
    window.addEventListener('pageshow', () => this.syncExpression(), {signal});
    window.addEventListener('blur', () => this.reset(), {signal});
    document.addEventListener('visibilitychange', () => {if(document.hidden)this.reset();}, {signal});
    for (const query of [this.motion,this.pointer]) query.addEventListener('change', () => this.reset(), {signal});
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {this.loadArt();this.observer.disconnect();}
      }, {rootMargin:'300px'});
      this.observer.observe(this);
    } else this.loadArt();
  }
  async loadArt() {
    if (this.loadingArt) return;
    this.loadingArt = true;
    const sprite = this.sprite;
    const sizes = `${Math.ceil(sprite.getBoundingClientRect().width*2172/709)}px`;
    const load = async (srcset,src) => {
      const image = new Image();
      image.sizes = sizes;
      image.srcset = srcset;
      image.src = src;
      await image.decode();
      return image.currentSrc || image.src;
    };
    const signal = this.abort.signal;
    load(sprite.dataset.srcset,sprite.dataset.src).then(url => {
      if (signal.aborted) return;
      sprite.style.backgroundImage = `url("${url}")`;
    }).catch(() => {if(!signal.aborted)sprite.style.backgroundImage=`url("${sprite.dataset.src}")`;});
    load(sprite.dataset.happySrcset,sprite.dataset.happySrc).then(url => {
      if (signal.aborted) return;
      sprite.style.setProperty('--fd-happy-sheet',`url("${url}")`);
      this.happyReady = true;
      this.syncExpression();
    }).catch(() => {});
  }
  draw(frame) {
    this.frame = frame;
    const position = `${MAIL_X[frame]}% 68%`;
    this.sprite.style.backgroundPosition = position;
    this.sprite.style.setProperty('--fd-happy-position',position);
    this.sprite.dataset.frame = frame;
    this.sprite.dataset.expression = this.happy && this.happyReady ? 'happy' : 'neutral';
  }
  syncExpression() {
    this.happy = Boolean(this.email?.value.trim() || this.querySelector('[data-dispatch-success]'));
    if (this.happy) this.reset(); else this.draw(this.frame);
  }
  cancel() { this.timers?.forEach(clearTimeout);this.timers=[]; }
  later(frame,delay) { this.timers.push(setTimeout(() => this.draw(frame),delay)); }
  reset() { this.cancel();if(this.sprite)this.draw(0); }
  offer(event) {
    if (this.happy || event.pointerType==='touch' || this.motion.matches || !this.pointer.matches || document.hidden) return;
    this.cancel();
    if (this.frame===0) {this.later(1,80);this.later(2,170);} else if(this.frame===1)this.later(2,90);
  }
  lower() {
    this.cancel();
    if(this.happy || this.motion.matches || this.frame===0){this.draw(0);return;}
    if(this.frame===2){this.draw(1);this.later(0,90);}else this.draw(0);
  }
  disconnectedCallback() {
    this.cancel();clearTimeout(this.resetTimer);this.abort?.abort();this.observer?.disconnect();this.loadingArt=false;
  }
}
if (!customElements.get('fd-header-duck')) customElements.define('fd-header-duck',FdHeaderDuck);
if (!customElements.get('fd-dispatch')) customElements.define('fd-dispatch',FdDispatch);
